<template>
  <div
    class="ideas-item"
    :class="{
      'result-step': isVotingStepResult
    }"
    :style="{'box-shadow': getBoxShadowAhaColor(item, index)}"
  >
    <div
      class="idea-content horizontal layout start"
    >
      <span
        class="idea-rank-container"
        v-if="isVotingStepResult"
        :style="{
          'box-shadow': getBoxShadowAhaColor(item, index),
          'background-color': getRankOrderBg(),
        }"
      >
        <span
          class="idea-rank font-bold"
          :class="{
            'custom-text-base': item.position <= 9,
            'custom-text-sm': item.position > 9 && item.position <= 99,
            'custom-text-xs': item.position > 99
          }"
        >
          {{ item.position }}
        </span>
      </span>
      <span
        class="audience-flex idea-text"
        :class="{'extra-pt': isVotingStepResult && item.position <= 9}"
      >{{ item.title | profanity }}
      </span>
    </div>
    <div
      class="idea-footer horizontal layout center"
      v-if="!hideAction"
      :class="{'gray': slide.textColour === '#313131'}"
    >
      <div class="audience-flex idea-name">
        <span v-if="item.audienceEmoji" class="custom-mr-1">{{ item.audienceEmoji }}</span>
        {{ item.audienceName | profanity }}
      </div>
      <div
        class="idea-vote horizontal layout center-justified center"
        @click="voteIdea(item)"
        v-if="slide.votingStep === voteStep.VOTING"
        :class="{'disabled': isDisableVote && !item.voted}"
      >
        <img src="@/assets/images/unlike.svg" v-if="votingIdea !== item.id && !item.voted">
        <img src="@/assets/images/like.svg" v-if="votingIdea !== item.id && item.voted">
        <ui-spinner :color="getLoadingColor" v-if="votingIdea === item.id" />
      </div>
      <div
        class="idea-vote idea-vote-result horizontal layout center-justified center"
        :class="{active: votingIdea !== item.id && item.voted}"
        v-if="isVotingStepResult"
      >
        <i class="fas fa-thumbs-up" /> <span>{{ item.votesCount | formatTotalPoint }}</span>
      </div>
    </div>
  </div>
</template>
<script>
import SlideOptionApi from '@/api/slide-option'
import UiSpinner from '@/components/UI/Spinner'
import * as Sentry from '@sentry/vue'
import storage from '@/services/storage'
import { mapGetters, mapState } from 'vuex'
import VoteStep from '@/constants/VoteStep'
import Util from '@/util'

export default {
  components: {
    UiSpinner,
  },
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    index: {
      type: Number,
      default: 0,
    },
    sortIdeas: {
      type: Function,
      default: () => {},
    },
    hideAction: {
      type: Boolean,
      default: false,
    },
    isDisableVote: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      votingIdea: null,
      voteStep: VoteStep,
    }
  },
  computed: {
    ...mapGetters({
      slide: 'activeActivity',
    }),
    ...mapState({
      presentation: (state) => state.presentation,
      audienceId: (state) => state.audienceId,
    }),
    getAhaColor() {
      return Util.getAhaColor(this.index)
    },
    getLoadingColor() {
      if (this.slide.textColour === '#313131') return 'black'
      return 'white'
    },
    isVotingStepVoting() {
      return this.slide.votingStep === this.voteStep.VOTING
    },
    isVotingStepResult() {
      return this.slide.votingStep === this.voteStep.RESULT
    },
  },
  methods: {
    getRankOrderBg() {
      return Util.hexToRgba(this.slide.baseColour)
    },
    getBoxShadowAhaColor(item, index) {
      return `0 0 0 1px ${item.color || Util.getAhaColor(index)}`
    },
    async voteIdea(idea) {
      if (this.votingIdea || (this.isDisableVote && !idea.voted)) return
      idea.voted = !idea.voted
      this.votingIdea = idea.id

      const payload = {
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        numberOfVotes: 1,
      }

      let ideasVotedIds = storage.getValueByPresentation(this.presentation.id, 'ideasVotedIds', [])
      try {
        if (idea.voted) {
          await SlideOptionApi.voteIdea(idea.id, payload)
          ideasVotedIds.push(idea.id)
        } else {
          await SlideOptionApi.unvoteIdea(idea.id, payload)
          ideasVotedIds = ideasVotedIds.filter((item) => item !== idea.id)
        }

        this.sortIdeas()
        storage.setValueByPresentation(this.presentation.id, 'ideasVotedIds', ideasVotedIds)

        this.votingIdea = null

        Util.mixpanelTrack(
          'Audience',
          `Audience > Ideas > Voted Idea ${idea.voted}`,
          `Audience vote idea ${idea.id}`,
        )
      } catch (error) {
        Sentry.captureException(error)
        this.votingIdea = null
        idea.voted = !idea.voted
        this.sortIdeas()
        Util.mixpanelTrack(
          'Audience',
          'Audience > Ideas > Voted Idea false',
          JSON.stringify(error),
        )
        Util.showToastError('Something when wrong, please try again!')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

.ideas-item {
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
  position: relative;
  &.result-step {
    overflow: visible;
    .idea-content {
      padding-top: 12px; // prevent margin collapsing
    }
  }
}

.idea-content {
  margin: 12px 16px;
  .idea-rank-container {
    position: absolute;
    top: -10px;
    left: -10px;
    width: 28px;
    height: 28px;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .idea-rank {
    font-size: 20px;
    font-weight: bold;
    &.custom-text-base {
      font-size: 16px;
    }
    &.custom-text-sm {
      font-size: 14px;
    }
    &.custom-text-xs {
      padding-top: 2px;
      font-size: 12px;
    }
  }
}

.idea-text {
  white-space: pre-line;
  word-break: break-word;
  &.extra-pt {
    padding-top: 2px;
  }
}

.idea-footer {
  background-color: rgba(240, 241, 241, 0.1);
  padding: 6px 16px;
  border-radius: 8px;

  &.gray {
    background-color: #f0f1f1;
  }

  .idea-name {
    font-size: 14px;
    font-weight: bold;
  }

  .idea-vote {
    margin-left: 12px;
    width: 26px;
    height: 26px;
    cursor: pointer;

    &.disabled {
      cursor: auto;
      opacity: 0.6;
    }

    img {
      width: 26px;
      height: 26px;
    }

    &.idea-vote-result {
      font-size: 14px;
      font-weight: bold;
      background-color: #fff;
      padding: 6px 12px;
      width: auto;
      height: 26px;
      border-radius: 20px;
      cursor: auto;
      color: #313131;

      i {
        font-size: 14px;
        margin-right: 4px;
        opacity: 0.4;
      }

      span {
        display: block;
        height: 100%;
        line-height: 16px;
      }

      &.active {
        background-color: $primary-color;
        color: #fff;

        i {
          opacity: 1;
        }
      }
    }
  }
}

</style>
